// src/theme/getTheme.js
import { createTheme } from '@mui/material/styles';

const getDesignTokens = () => ({
  palette: {
    // Always light
    mode: 'light',

    // Palette B - Material Deep Orange + Amber
    primary: {
      main: '#FB8C00',   // deep orange
      light: '#FFB74D',
      dark: '#EF6C00',
      contrastText: '#FFFFFF',
    },

    secondary: {
      main: '#FFA000',   // amber
      light: '#FFECB3',
      dark: '#FF6F00',
      contrastText: '#FFFFFF',
    },

    // Official MUI background keys
    background: {
      default: '#FFF', // warm off-white
      paper: '#FFFFFF',
    },

    // Custom keys you asked to keep (not standard MUI keys but kept for compatibility)
    bg: {
      default: '#FFFFF',
      paper: '#FFFFFF',
    },

    // Text & divider
    text: {
      primary: '#2B2B2B',
      secondary: '#6B6B6B',
    },

    divider: 'rgba(251, 140, 0, 0.17)',

    // Extra accents used by some component overrides (kept to avoid undefined)
    accent: {
      pink: '#FFCDD2',
      mint: '#C8E6C9',
      gold: '#FFE0B2',
      orange: '#FFD180',
      aqua: '#B2EBF2',
    },
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
    },
  },

  typography: {
    fontFamily: '"Nunito Sans", sans-serif',
    h1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 700,
      fontSize: '6rem',
      lineHeight: 1.2,
      '@media (max-width:1920px)': { fontSize: '5rem' },
      '@media (max-width:1536px)': { fontSize: '4.5rem' },
      '@media (max-width:1200px)': { fontSize: '4rem' },
      '@media (max-width:900px)': { fontSize: '3.5rem' },
      '@media (max-width:600px)': { fontSize: '3rem' },
      '@media (max-width:400px)': { fontSize: '2.5rem' },
    },
    h2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      fontSize: '4.5rem',
      lineHeight: 1.3,
      '@media (max-width:1920px)': { fontSize: '4rem' },
      '@media (max-width:1536px)': { fontSize: '3.5rem' },
      '@media (max-width:1200px)': { fontSize: '3rem' },
      '@media (max-width:900px)': { fontSize: '2.5rem' },
      '@media (max-width:600px)': { fontSize: '2rem' },
      '@media (max-width:400px)': { fontSize: '1.8rem' },
    },
    h3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '3.5rem',
      lineHeight: 1.4,
      '@media (max-width:1920px)': { fontSize: '3rem' },
      '@media (max-width:1536px)': { fontSize: '2.5rem' },
      '@media (max-width:1200px)': { fontSize: '2.2rem' },
      '@media (max-width:900px)': { fontSize: '2rem' },
      '@media (max-width:600px)': { fontSize: '1.8rem' },
      '@media (max-width:400px)': { fontSize: '1.6rem' },
    },
    h4: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '2.5rem',
      lineHeight: 1.4,
      '@media (max-width:1920px)': { fontSize: '2.2rem' },
      '@media (max-width:1536px)': { fontSize: '2rem' },
      '@media (max-width:1200px)': { fontSize: '1.8rem' },
      '@media (max-width:900px)': { fontSize: '1.6rem' },
      '@media (max-width:600px)': { fontSize: '1.4rem' },
      '@media (max-width:400px)': { fontSize: '1.2rem' },
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 500,
      fontSize: '2rem',
      lineHeight: 1.5,
      '@media (max-width:1920px)': { fontSize: '1.8rem' },
      '@media (max-width:1536px)': { fontSize: '1.6rem' },
      '@media (max-width:1200px)': { fontSize: '1.4rem' },
      '@media (max-width:900px)': { fontSize: '1.3rem' },
      '@media (max-width:600px)': { fontSize: '1.2rem' },
      '@media (max-width:400px)': { fontSize: '1.1rem' },
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.5,
      '@media (max-width:1920px)': { fontSize: '1.4rem' },
      '@media (max-width:1536px)': { fontSize: '1.3rem' },
      '@media (max-width:1200px)': { fontSize: '1.2rem' },
      '@media (max-width:900px)': { fontSize: '1.1rem' },
      '@media (max-width:600px)': { fontSize: '1rem' },
      '@media (max-width:400px)': { fontSize: '0.9rem' },
    },
    subtitle1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      '@media (max-width:1920px)': { fontSize: '1.2rem' },
      '@media (max-width:1536px)': { fontSize: '1.15rem' },
      '@media (max-width:1200px)': { fontSize: '1.1rem' },
      '@media (max-width:900px)': { fontSize: '1rem' },
      '@media (max-width:600px)': { fontSize: '0.95rem' },
      '@media (max-width:400px)': { fontSize: '0.9rem' },
    },
    subtitle2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
      fontSize: '1.1rem',
      lineHeight: 1.6,
      '@media (max-width:1920px)': { fontSize: '1.05rem' },
      '@media (max-width:1536px)': { fontSize: '1rem' },
      '@media (max-width:1200px)': { fontSize: '0.95rem' },
      '@media (max-width:900px)': { fontSize: '0.9rem' },
      '@media (max-width:600px)': { fontSize: '0.85rem' },
      '@media (max-width:400px)': { fontSize: '0.8rem' },
    },
    body1: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.7,
      '@media (max-width:1920px)': { fontSize: '0.95rem' },
      '@media (max-width:1536px)': { fontSize: '0.9rem' },
      '@media (max-width:1200px)': { fontSize: '0.85rem' },
      '@media (max-width:900px)': { fontSize: '0.9rem' },
      '@media (max-width:600px)': { fontSize: '0.85rem' },
      '@media (max-width:400px)': { fontSize: '0.8rem' },
    },
    body2: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.7,
      '@media (max-width:1920px)': { fontSize: '0.85rem' },
      '@media (max-width:1536px)': { fontSize: '0.8rem' },
      '@media (max-width:1200px)': { fontSize: '0.8rem' },
      '@media (max-width:900px)': { fontSize: '0.85rem' },
      '@media (max-width:600px)': { fontSize: '0.8rem' },
      '@media (max-width:400px)': { fontSize: '0.75rem' },
    },
    caption: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      '@media (max-width:1920px)': { fontSize: '0.7rem' },
      '@media (max-width:1536px)': { fontSize: '0.65rem' },
      '@media (max-width:1200px)': { fontSize: '0.6rem' },
      '@media (max-width:900px)': { fontSize: '0.65rem' },
      '@media (max-width:600px)': { fontSize: '0.6rem' },
      '@media (max-width:400px)': { fontSize: '0.55rem' },
    },
    overline: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 500,
      fontSize: '0.625rem',
      lineHeight: 1.66,
      textTransform: 'uppercase',
      '@media (max-width:1920px)': { fontSize: '0.6rem' },
      '@media (max-width:1536px)': { fontSize: '0.55rem' },
      '@media (max-width:1200px)': { fontSize: '0.5rem' },
      '@media (max-width:900px)': { fontSize: '0.55rem' },
      '@media (max-width:600px)': { fontSize: '0.5rem' },
      '@media (max-width:400px)': { fontSize: '0.45rem' },
    },
    disclaimer: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 400,
      fontSize: '0.625rem',
      lineHeight: 1.5,
      '@media (max-width:1920px)': { fontSize: '0.6rem', lineHeight: 1.4 },
      '@media (max-width:1536px)': { fontSize: '0.55rem', lineHeight: 1.3 },
      '@media (max-width:1200px)': { fontSize: '0.5rem', lineHeight: 1.2 },
      '@media (max-width:900px)': { fontSize: '0.55rem', lineHeight: 1.2 },
      '@media (max-width:600px)': { fontSize: '0.5rem', lineHeight: 1.1 },
      '@media (max-width:400px)': { fontSize: '0.45rem', lineHeight: 1.1 },
    },
    button: {
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      '@media (max-width:1920px)': { fontSize: '0.85rem' },
      '@media (max-width:1536px)': { fontSize: '0.8rem' },
      '@media (max-width:1200px)': { fontSize: '0.75rem' },
      '@media (max-width:900px)': { fontSize: '0.8rem' },
      '@media (max-width:600px)': { fontSize: '0.75rem' },
      '@media (max-width:400px)': { fontSize: '0.7rem' },
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '50px',
          padding: '10px 20px',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 4px 14px rgba(239, 108, 0, 0.28)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: '100px',
          height: '40px',
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
            boxShadow: '0 6px 20px rgba(239, 108, 0, 0.34)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 2px 8px rgba(239, 108, 0, 0.18)',
          },
          '&:disabled': {
            background: 'rgba(239, 108, 0, 0.14)',
            color: 'rgba(255, 255, 255, 0.6)',
          },
          '@media (max-width:600px)': {
            padding: '8px 16px',
            height: '36px',
            minWidth: '80px',
          },
          '@media (max-width:400px)': {
            padding: '6px 12px',
            height: '32px',
            minWidth: '70px',
          },
        }),
        contained: ({ theme }) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }),
        outlined: ({ theme }) => ({
          border: `2px solid ${theme.palette.primary.main}`,
          background: 'transparent',
          color: theme.palette.primary.main,
          '&:hover': {
            background: `${theme.palette.primary.main}15`,
            borderColor: theme.palette.primary.light,
          },
        }),
        text: ({ theme }) => ({
          background: 'transparent',
          color: theme.palette.primary.main,
          '&:hover': {
            background: `${theme.palette.primary.main}10`,
          },
        }),
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-input': {
            color: theme.palette.text.primary,
            borderRadius: '50px',
            padding: '12px 16px',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            background: 'rgba(255, 248, 245, 0.6)',
            '& fieldset': {
              borderRadius: '50px',
              borderColor: `${theme.palette.primary.main}40`,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.light,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
          '& label': {
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            '@media (max-width:600px)': { fontSize: '0.9rem' },
            '@media (max-width:400px)': { fontSize: '0.8rem' },
          },
          '& .MuiInputBase-input::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 0.7,
          },
        }),
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '50px',
          background: 'rgba(255, 248, 245, 0.6)',
          color: theme.palette.text.primary,
          fontWeight: 500,
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '1rem',
          '& .MuiSelect-select': {
            borderRadius: '50px',
            padding: '12px 20px',
            '@media (max-width:600px)': { padding: '10px 16px' },
            '@media (max-width:400px)': { padding: '8px 12px' },
          },
          '& fieldset': {
            borderRadius: '50px',
            borderColor: `${theme.palette.primary.main}40`,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.primary.light,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
          '& .MuiSelect-icon': {
            color: theme.palette.primary.main,
            right: '10px',
            '@media (max-width:600px)': { right: '8px' },
            '@media (max-width:400px)': { right: '6px' },
          },
        }),
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '25px',
          margin: '4px 8px',
          fontWeight: 500,
          fontSize: '1rem',
          color: theme.palette.text.primary,
          padding: '8px 16px',
          '&:hover': {
            background: `${theme.palette.primary.main}15`,
          },
          '&.Mui-selected': {
            background: `${theme.palette.primary.main}25`,
            '&:hover': {
              background: `${theme.palette.primary.main}35`,
            },
          },
          '@media (max-width:600px)': {
            fontSize: '0.9rem',
            padding: '6px 12px',
          },
          '@media (max-width:400px)': {
            fontSize: '0.8rem',
            padding: '5px 10px',
          },
        }),
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontWeight: 500,
          fontSize: '1rem',
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
          '@media (max-width:600px)': { fontSize: '0.9rem' },
          '@media (max-width:400px)': { fontSize: '0.8rem' },
        }),
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '50px',
          '& fieldset': {
            borderColor: `${theme.palette.primary.main}40`,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.primary.light,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
        }),
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: '20px',
          marginTop: '8px',
          background: theme.palette.background.paper,
          boxShadow: '0 6px 20px rgba(239, 108, 0, 0.18)',
          border: `1px solid ${theme.palette.divider}`,
          '@media (max-width:600px)': { marginTop: '6px' },
          '@media (max-width:400px)': { marginTop: '4px' },
        }),
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '24px',
          padding: '24px',
          background: `linear-gradient(145deg, ${theme.palette.bg.paper}, rgba(255, 244, 238, 0.6))`,
          color: theme.palette.text.primary,
          boxShadow: '0 10px 30px rgba(239, 108, 0, 0.12)',
          border: `1px solid ${theme.palette.divider}`,
          '@media (max-width:600px)': { padding: '16px' },
          '@media (max-width:400px)': { padding: '12px' },
        }),
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '24px',
          transition: 'all 0.3s ease',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          '&:before': { display: 'none' },
          boxShadow: '0 4px 14px rgba(239, 108, 0, 0.06)',
          '@media (max-width:600px)': { borderRadius: '16px' },
          '@media (max-width:400px)': { borderRadius: '12px' },
        }),
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
          padding: '16px 24px',
          borderRadius: '24px',
          color: theme.palette.text.primary,
          '@media (max-width:600px)': { padding: '12px 16px' },
          '@media (max-width:400px)': { padding: '10px 12px' },
        }),
      },
    },

    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderTop: `1px solid ${theme.palette.divider}`,
          padding: '16px 24px',
          '@media (max-width:600px)': { padding: '12px 16px' },
          '@media (max-width:400px)': { padding: '10px 12px' },
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.875rem',
          borderRadius: '50px',
          color: theme.palette.primary.contrastText,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          height: '32px',
          padding: '0 12px',
          '@media (max-width:600px)': {
            fontSize: '0.8rem',
            height: '28px',
            padding: '0 10px',
          },
          '@media (max-width:400px)': {
            fontSize: '0.75rem',
            height: '24px',
            padding: '0 8px',
          },
        }),
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '40px',
          height: '40px',
          fontSize: '1.25rem',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: theme.palette.primary.contrastText,
          '@media (max-width:600px)': {
            width: '36px',
            height: '36px',
            fontSize: '1.1rem',
          },
          '@media (max-width:400px)': {
            width: '32px',
            height: '32px',
            fontSize: '1rem',
          },
        }),
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '16px',
          padding: '12px 16px',
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, rgba(239, 108, 0, 0.06))`,
          '@media (max-width:600px)': { padding: '10px 12px' },
          '@media (max-width:400px)': { padding: '8px 10px' },
        }),
        standardSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.light,
          color: theme.palette.secondary.contrastText,
        }),
        standardError: ({ theme }) => ({
          backgroundColor: theme.palette.accent.pink,
          color: '#FFFFFF',
        }),
        standardWarning: ({ theme }) => ({
          backgroundColor: theme.palette.accent.orange,
          color: '#FFFFFF',
        }),
        standardInfo: ({ theme }) => ({
          backgroundColor: theme.palette.accent.aqua,
          color: '#FFFFFF',
        }),
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: '24px',
          background: theme.palette.background.paper,
          boxShadow: '0 10px 30px rgba(239, 108, 0, 0.12)',
          padding: '24px',
          '@media (max-width:600px)': { borderRadius: '16px', padding: '16px' },
          '@media (max-width:400px)': { borderRadius: '12px', padding: '12px' },
        }),
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          fontSize: '1.5rem',
          color: theme.palette.text.primary,
          padding: '16px 24px',
          '@media (max-width:600px)': { fontSize: '1.3rem', padding: '12px 16px' },
          '@media (max-width:400px)': { fontSize: '1.2rem', padding: '10px 12px' },
        }),
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '16px 24px',
          color: theme.palette.text.primary,
          '@media (max-width:600px)': { padding: '12px 16px' },
          '@media (max-width:400px)': { padding: '10px 12px' },
        }),
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '16px 24px',
          '@media (max-width:600px)': { padding: '12px 16px' },
          '@media (max-width:400px)': { padding: '10px 12px' },
        }),
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontSize: '0.875rem',
          borderRadius: '8px',
          padding: '8px 12px',
          '@media (max-width:600px)': { fontSize: '0.8rem', padding: '6px 10px' },
          '@media (max-width:400px)': { fontSize: '0.75rem', padding: '5px 8px' },
        }),
        arrow: ({ theme }) => ({ color: theme.palette.primary.main }),
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 46,
          height: 26,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: theme.palette.common.white,
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': { width: 22, height: 22 },
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E2E8F0',
            opacity: 1,
          },
          '@media (max-width:600px)': {
            width: 40,
            height: 24,
            '& .MuiSwitch-thumb': { width: 20, height: 20 },
            '& .MuiSwitch-switchBase': {
              padding: 2,
              '&.Mui-checked': { transform: 'translateX(16px)' },
            },
          },
          '@media (max-width:400px)': {
            width: 36,
            height: 22,
            '& .MuiSwitch-thumb': { width: 18, height: 18 },
            '& .MuiSwitch-switchBase': {
              padding: 2,
              '&.Mui-checked': { transform: 'translateX(14px)' },
            },
          },
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({ borderBottom: `1px solid ${theme.palette.divider}` }),
        indicator: ({ theme }) => ({ backgroundColor: theme.palette.primary.main, height: '3px' }),
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 600,
          fontSize: '1rem',
          textTransform: 'none',
          color: theme.palette.text.secondary,
          '&.Mui-selected': { color: theme.palette.primary.main },
          '@media (max-width:600px)': { fontSize: '0.9rem' },
          '@media (max-width:400px)': { fontSize: '0.8rem' },
        }),
      },
    },

    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          overflow: 'hidden',
        }),
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          color: theme.palette.text.primary,
          '@media (max-width:600px)': { padding: '10px 12px' },
          '@media (max-width:400px)': { padding: '8px 10px' },
        }),
        head: ({ theme }) => ({
          fontWeight: 600,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
        }),
      },
    },

    MuiPagination: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPaginationItem-root': {
            borderRadius: '50px',
            color: theme.palette.text.primary,
            '&.Mui-selected': {
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: theme.palette.primary.contrastText,
            },
            '&:hover': { background: `${theme.palette.primary.main}15` },
            '@media (max-width:600px)': { padding: '8px' },
            '@media (max-width:400px)': { padding: '6px' },
          },
        }),
      },
    },

    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          height: 8,
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: theme.palette.common.white,
            border: `2px solid ${theme.palette.primary.main}`,
            '&:hover': { boxShadow: `0 0 0 8px ${theme.palette.primary.main}20` },
          },
          '& .MuiSlider-track': { height: 8, borderRadius: 4 },
          '& .MuiSlider-rail': {
            height: 8,
            borderRadius: 4,
            backgroundColor: '#E2E8F0',
          },
          '@media (max-width:600px)': {
            height: 6,
            '& .MuiSlider-thumb': { width: 14, height: 14 },
            '& .MuiSlider-track': { height: 6 },
            '& .MuiSlider-rail': { height: 6 },
          },
          '@media (max-width:400px)': {
            height: 5,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
            '& .MuiSlider-track': { height: 5 },
            '& .MuiSlider-rail': { height: 5 },
          },
        }),
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-root': {
            borderRadius: '50px',
            background: 'rgba(255, 248, 245, 0.6)',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: `${theme.palette.primary.main}40` },
            '&:hover fieldset': { borderColor: theme.palette.primary.light },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }),
        paper: ({ theme }) => ({
          borderRadius: '20px',
          boxShadow: '0 6px 20px rgba(239, 108, 0, 0.12)',
          background: theme.palette.background.paper,
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.background.paper,
          borderRadius: '16px',
          boxShadow: '0 4px 14px rgba(239, 108, 0, 0.06)',
          '@media (max-width:600px)': { borderRadius: '12px' },
          '@media (max-width:400px)': { borderRadius: '10px' },
        }),
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({ color: theme.palette.text.primary }),
      },
    },
  },
});

// Final export: createTheme and return it
export default function getTheme() {
  return createTheme(getDesignTokens());
}
